import { useState } from 'react'
import { FiInfo } from 'react-icons/fi'
import posthog from 'posthog-js'
import { faFull, faPlain, toman } from 'utilities/helper/format'
import {
    demandPriority,
    demandPriorityTitle,
} from 'utilities/helper/demandPriority'
import ScoreMeter from './ScoreMeter'

/**
 * A description longer than this gets clamped to two lines with a «متن کامل».
 * A character count rather than a measured overflow: the row renders in a
 * list of dozens, and one ResizeObserver per row to decide whether to offer a
 * toggle is a lot of machinery for a two-line paragraph.
 */
const NOTE_CLAMP = 90

/**
 * One member in the «گیرندهٔ وام» list.
 *
 * The row is ordered the way the decision is made: name and rank, then the
 * score bar, then the facts that change the answer — what they asked for,
 * how soon they need it, why they asked, and whether they currently owe.
 * Everything else lives behind the (i) or on the next screen; a list a
 * manager has to read carefully is a list they will not read at all.
 *
 * فوریت and the member's own note are on the row rather than one screen in,
 * because they are the two things that decide *which* request to serve first,
 * and the member was told when they wrote the note that managers would read
 * it. The note is clamped, not hidden: two lines answer «why» for almost
 * every real request, and the rest is one tap away without leaving the list.
 *
 * Trouble is stated, never scored away: an overdue member can still be picked
 * (funds lend to people in difficulty on purpose), so the red line informs
 * rather than blocks.
 */
export default function CandidateRow({
    candidate,
    moneypoolId,
    isSelected,
    onSelect,
    onExplain,
}: {
    candidate: ILoanCandidate
    moneypoolId?: string
    isSelected: boolean
    onSelect: () => void
    onExplain: () => void
}) {
    const [showFullNote, setShowFullNote] = useState(false)
    const { demand } = candidate
    const priority = demandPriority(demand?.priority)
    const note = demand?.description?.trim() ?? ''
    const isLongNote = note.length > NOTE_CLAMP

    // Whether two lines were enough. If managers open almost every note, the
    // clamp is in the wrong place; if nobody opens any, the note belongs one
    // screen in. Only the length travels — never the text.
    const toggleNote = () =>
        setShowFullNote((open) => {
            if (!open) {
                posthog.capture('loan_demand_note_expanded', {
                    fund_id: moneypoolId,
                    poolship_id: candidate.poolship_id,
                    note_length: note.length,
                    demand_priority: demand?.priority || null,
                })
            }
            return !open
        })

    return (
        <div
            role="button"
            tabIndex={0}
            aria-pressed={isSelected}
            onClick={onSelect}
            onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault()
                    onSelect()
                }
            }}
            className={` w-full cursor-pointer rounded-2xl border bg-white p-3 text-right transition-colors ${
                isSelected
                    ? ' border-brand shadow-card'
                    : ' border-stroke active:bg-canvas'
            }`}
        >
            <div className=" flex items-center justify-between gap-2">
                <p className=" truncate text-sm font-bold text-heading">
                    {candidate.member_name}
                </p>
                <span className=" shrink-0 text-[11px] text-text-secondary">
                    رتبه {faPlain.format(candidate.rank)}
                </span>
            </div>

            <div className=" mt-2 flex items-center gap-2">
                <div className=" flex-1">
                    <ScoreMeter percentile={candidate.percentile} />
                </div>
                {/* its own button, not a nested one — the row is a role=button */}
                <span
                    role="button"
                    tabIndex={0}
                    aria-label={`توضیح امتیاز ${candidate.member_name}`}
                    onClick={(event) => {
                        event.stopPropagation()
                        onExplain()
                    }}
                    onKeyDown={(event) => {
                        if (event.key === 'Enter' || event.key === ' ') {
                            event.preventDefault()
                            event.stopPropagation()
                            onExplain()
                        }
                    }}
                    className=" -m-1 shrink-0 cursor-pointer p-1 text-text-muted"
                >
                    <FiInfo size={15} />
                </span>
            </div>

            <p className=" mt-2 text-[11px] text-text-secondary">
                سرمایه {toman(candidate.capital)}
                {candidate.loan_outstanding > 0 && (
                    <> · بدهی {toman(candidate.loan_outstanding)}</>
                )}
            </p>

            {demand && (
                <>
                    {/* the chip rides on the amount line rather than taking a
                        line of its own: it is one word, and the two facts
                        belong to the same request */}
                    <div className=" mt-1 flex items-start justify-between gap-2">
                        <p className=" text-[11px] font-bold text-brand-deep">
                            درخواست {toman(demand.amount)} در{' '}
                            {faPlain.format(demand.num_repayments)} قسط
                        </p>
                        {priority && (
                            <span
                                title={demandPriorityTitle(priority)}
                                aria-label={demandPriorityTitle(priority)}
                                className={` shrink-0 rounded-full px-2 py-0.5 text-[10px] font-bold${priority.chipClass}`}
                            >
                                {priority.label}
                            </span>
                        )}
                    </div>
                    {/* its own line, not a «·» separator: a middle dot sitting
                        against a digit reads as an extra zero at phone size.
                        The Persian comma is safe — it never reads as a digit. */}
                    {(priority || demand.waiting_days > 0) && (
                        <p className=" text-[10px] text-text-secondary">
                            {priority?.need}
                            {priority && demand.waiting_days > 0 && '، '}
                            {demand.waiting_days > 0 &&
                                `${faPlain.format(
                                    demand.waiting_days
                                )} روز در انتظار`}
                        </p>
                    )}
                    {note && (
                        <div className=" mt-1.5 rounded-lg bg-canvas px-2 py-1.5">
                            <p
                                className={` text-[11px] leading-5 text-text-body${
                                    isLongNote && !showFullNote
                                        ? ' line-clamp-2'
                                        : ''
                                }`}
                            >
                                «{note}»
                            </p>
                            {isLongNote && (
                                <span
                                    role="button"
                                    tabIndex={0}
                                    aria-expanded={showFullNote}
                                    aria-label={`${
                                        showFullNote ? 'بستن' : 'متن کامل'
                                    } توضیح ${candidate.member_name}`}
                                    onClick={(event) => {
                                        event.stopPropagation()
                                        toggleNote()
                                    }}
                                    onKeyDown={(event) => {
                                        if (
                                            event.key === 'Enter' ||
                                            event.key === ' '
                                        ) {
                                            event.preventDefault()
                                            event.stopPropagation()
                                            toggleNote()
                                        }
                                    }}
                                    className=" mt-1 inline-block cursor-pointer text-[10px] font-bold text-brand-deep"
                                >
                                    {showFullNote ? 'کمتر' : 'متن کامل'}
                                </span>
                            )}
                        </div>
                    )}
                </>
            )}

            {candidate.overdue_count > 0 && (
                <p className=" mt-1.5 rounded-lg bg-danger-soft px-2 py-1 text-[11px] font-bold text-danger-on">
                    {faPlain.format(candidate.overdue_count)} قسط معوق —{' '}
                    {faFull.format(candidate.overdue_amount)} تومان، تا{' '}
                    {faPlain.format(candidate.max_days_overdue)} روز تاخیر
                </p>
            )}
        </div>
    )
}
